import {Injectable} from '@angular/core';
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {ChartData} from "../models/chart-data";
import {ChartGetDataModel, ChartSetDataModel} from "../models/amchart/chart-exchange";
import {ChartListProps} from "../models/amchart/chart-list-props";
import {AddChartModel} from "../models/amchart/add-chart-model";
import {ChartLineModel} from "../models/amchart/chart-line-model";
import {CodeNameChartTabs} from "../models/code-name-chart-tabs";
import {ChartDataGUID} from "../models/chart-data-GUID";
import {TabDataGUID} from "../models/tab-data-GUID";
import {TabsModeGUID} from "../models/tabs-mode-GUID";
import {ChartHelper} from "../models/chart-helper";

@Injectable()
export class BufferGraphDataExchangeService{

    private newChartDataSource = new BehaviorSubject(null);
    newChartDataCurrent = this.newChartDataSource.asObservable();

    private showTargetLevelSource = new BehaviorSubject(null);
    showTargetLevelCurrent = this.showTargetLevelSource.asObservable();

    private updateChartDataSource = new BehaviorSubject(null);
    updateChartDataCurrent = this.updateChartDataSource.asObservable();

    private chartGetDataSource = new BehaviorSubject([]);
    chartGetDataCurrent = this.chartGetDataSource.asObservable();

    private chartListPropsSource = new BehaviorSubject([]);
    chartListPropsCurrent = this.chartListPropsSource.asObservable();

    private addCompareChartSource = new BehaviorSubject(null);
    addCompareChartCurrent = this.addCompareChartSource.asObservable();

    private toggleBubbleChartSource = new BehaviorSubject(null);
    toggleBubbleChartCurrent = this.toggleBubbleChartSource.asObservable();

    private toggleChartInfoSource = new BehaviorSubject(null);
    toggleChartInfoCurrent = this.toggleChartInfoSource.asObservable();

    private toggleLinesVisibilitySource = new BehaviorSubject(null);
    toggleLinesVisibilityCurrent = this.toggleLinesVisibilitySource.asObservable();

    private updateChartZoomSource = new BehaviorSubject(null);
    updateChartZoomCurrent = this.updateChartZoomSource.asObservable();

    private customChartPopperUpdateSource = new BehaviorSubject(null);
    customChartPopperUpdateCurrent = this.customChartPopperUpdateSource.asObservable();

    private compareChartReadySource = new BehaviorSubject(null);
    compareChartReadyCurrent = this.compareChartReadySource.asObservable();

    private tabsResizeSource = new BehaviorSubject(null);
    tabsResizeCurrent = this.tabsResizeSource.asObservable();

    private tabsListSource = new BehaviorSubject([]);
    tabsListCurrent = this.tabsListSource.asObservable();

    private tabsListGUIDSource = new BehaviorSubject('');
    tabsListGUIDCurrent = this.tabsListGUIDSource.asObservable();

    private chartDataSource = new BehaviorSubject([]);
    chartDataCurrent = this.chartDataSource.asObservable();

    private tabsDataSource = new BehaviorSubject([]);
    tabsDataCurrent = this.tabsDataSource.asObservable();

    private tabsModeSource = new BehaviorSubject([]);
    tabsModeCurrent = this.tabsModeSource.asObservable();

    private glassSource = new BehaviorSubject(null);
    glassCurrent = this.glassSource.asObservable();

    private helper: ChartHelper;


    constructor(){
        this.helper = new ChartHelper();
    }

    // setRegistryType = (registryType: number): void =>{
    //     this.registryTypeSource.next(registryType);
    // };
    //
    // getRegistryType = () =>{
    //     return this.registryTypeCurrent
    // };

    //# send a request for data for the chart
    setNewChartData = (data: ChartGetDataModel): void =>{
        this.newChartDataSource.next(data);
    };

    //# show target level on line chart
    setShowTargetLevel = (show: boolean): void =>{
        this.showTargetLevelSource.next(show);
    };

    //# send a request for update data for the chart and tabs
    setUpdateChartData = (chartData: ChartSetDataModel): void =>{
        this.updateChartDataSource.next(chartData);
        this.updateChartDataSource.next(null);
    };

    //# save chart getData in service (in period-selector component)
    setChartGetData = (chartGetData: ChartGetDataModel[]): void =>{
        this.chartGetDataSource.next(chartGetData);
    };

    //# get chart getData in service (in period-selector component)
    getChartGetData = (): ChartGetDataModel[] =>{
        return this.chartGetDataSource.getValue();
    };

    //# data bank for period-selector (set filter restrictions for sku and warehouse filter)
    setChartListProps = (chartGetData: ChartListProps[]): void =>{
        this.chartListPropsSource.next(chartGetData);
    };

    //# data bank for period-selector (set filter restrictions for sku and warehouse filter)
    getChartListProps = (): ChartListProps[] => {
        return this.chartListPropsSource.getValue();
    };

    //# send a request for add compare chart
    setAddChart = (chart: AddChartModel): void =>{
        this.addCompareChartSource.next(chart);
    };

    //# toggle bubble chart visibility
    setToggleBubbleChart = (data: {type: string, sharedGUID: string, show: boolean}): void =>{
        this.toggleBubbleChartSource.next(data);
    };

    //# toggle info in chart visibility
    setToggleChartInfo = (data: {type: string, sharedGUID: string, show: boolean}): void =>{
        this.toggleChartInfoSource.next(data);
    };

    //# sync line visibility on charts main and compare
    setToggleLinesVisibility = (data: {type: string, sharedGUID: string, lineName: string}): void =>{
        this.toggleLinesVisibilitySource.next(data);
        this.toggleLinesVisibilitySource.next(null);
    };

    //# sync zoom on charts main and compare
    setUpdateChartZoom = (data: {type: string, sharedGUID: string, start: Date, end: Date}): void =>{
        this.updateChartZoomSource.next(data);
    };

    //# update add compare charts popper if chart dimension changes
    setCustomChartPopperUpdate = (val: boolean): void =>{
        this.customChartPopperUpdateSource.next(val);
    };

    //# notify when compare chart ready,
    setCompareChartReady = (data: {command: string, type: string, sharedGUID: string, lines: ChartLineModel[]}): void =>{
        this.compareChartReadySource.next(data);
        this.compareChartReadySource.next(null);
    };

    //# update tab dimensions, update grid inside tabs
    notifyTabsResize = (sharedGUID: string): void =>{
        this.tabsResizeSource.next(sharedGUID);
    };

    //# add or update tablist
    setTabsList = (tabList: CodeNameChartTabs): void =>{
        let allTabsData = this.tabsListSource.getValue(),
            set: boolean = false;
        allTabsData.forEach((item, i, arr) => {
            if (item.GUID === tabList.GUID){
                arr[i] = tabList;
                set = true;
            }
        });

        // console.trace(set, allTabsData, 'setTabsList')

        !set ? allTabsData.push(tabList) : null;
        // console.log(set, allTabsData, 'setTabsList');

        this.tabsListSource.next(allTabsData);
        this.tabsListGUIDSource.next(tabList.GUID);
        // this.tabsListGUIDSource.next('');
    };

    //# get needed tablist
    getTabsList = (GUID: string): CodeNameChartTabs =>{
        let allTabsData: CodeNameChartTabs[] = this.tabsListSource.getValue();
        return allTabsData ? allTabsData.find(item => item.GUID === GUID) : null;
        // return allTabsData ? allTabsData.filter(item => item.GUID === GUID).find(item => item) : null;
    };

    //# get needed tablist
    getTabsList2 = (): CodeNameChartTabs[] =>{
        return this.tabsListSource.getValue();

    };

    //# save chart data to service
    setChartData = (chartData: ChartDataGUID): void =>{
        let allChartData: ChartDataGUID[]  = this.chartDataSource.getValue(),
            set: boolean = false;
        allChartData.forEach((item, i, arr) => {
            if (item.GUID === chartData.GUID){
                arr[i] = chartData;
                set = true;
            }
        });

        !set ? allChartData.push(chartData) : null;
        this.chartDataSource.next(allChartData);
    };

    //# get chart data from service
    getChartData = (GUID: string, itemId: number): ChartDataGUID =>{
        let allChartData: ChartDataGUID[] = this.chartDataSource.getValue();
        return allChartData && allChartData.length ? allChartData.find(item => item.GUID === GUID && item.data.itemInfo.itemId === itemId) : null;
    };

    //# remove chart data from service
    delChartData = (GUID: string): void =>{
        // console.trace('delTabsData')
        let allChartData: TabsModeGUID[] = this.chartDataSource.getValue();
        this.helper.remFromArr(allChartData, 'GUID', GUID);
        this.chartDataSource.next(allChartData);
    };

    //# save tabs data to service
    setTabsData = (tabData: TabDataGUID): void =>{
        let allTabsData: TabDataGUID[]  = this.tabsDataSource.getValue(),
            set: boolean = false;
        allTabsData.forEach((item, i, arr) => {
            if (item.GUID === tabData.GUID){
                arr[i] = tabData;
                set = true;
            }
        });

        !set ? allTabsData.push(tabData) : null;
        this.tabsDataSource.next(allTabsData);
    };

    //# get tabs data from service
    getTabsData = (GUID: string, itemId: number): TabDataGUID =>{
        let allTabsData: TabDataGUID[] = this.tabsDataSource.getValue();
        return allTabsData && allTabsData.length ? allTabsData.find(item => item.GUID === GUID && item.data.length && item.data[0].itemInfo.itemId === itemId) : null;
    };

    //# remove tabs data from service
    delTabsData = (GUID: string): void =>{
        // console.trace('delTabsData')
        let allTabsData: TabsModeGUID[] = this.tabsDataSource.getValue();
        this.helper.remFromArr(allTabsData, 'GUID', GUID);
        this.tabsDataSource.next(allTabsData);
    };

    //# save tabs display settins to service
    setTabsMode = (tabsMode: TabsModeGUID): void =>{
        let allTabsMode: TabsModeGUID[]  = this.tabsModeSource.getValue(),
            set: boolean = false;
        allTabsMode.forEach((item, i, arr) => {
            if (item.GUID === tabsMode.GUID){
                arr[i] = tabsMode;
                set = true;
            }
        });

        !set ? allTabsMode.push(tabsMode) : null;
        this.tabsModeSource.next(allTabsMode);
    };

    //# get tabs display settings from service
    getTabsMode = (GUID: string): TabsModeGUID =>{
        let tabsMode: TabsModeGUID[] = this.tabsModeSource.getValue();
        return tabsMode && tabsMode.length ? tabsMode.find(item => item.GUID === GUID) : null;
    };

    //# remove tabs display settings from service
    delTabsMode = (GUID: string): void =>{
        let allTabsMode: TabsModeGUID[] = this.tabsModeSource.getValue();
        this.helper.remFromArr(allTabsMode, 'GUID', GUID);
        this.tabsModeSource.next(allTabsMode);
    };

    //# notify set glass to chart,
    setGlass = (data: {sharedGUID: string, showGlass: boolean}): void =>{
        this.glassSource.next(data);
    };
}






// WEBPACK FOOTER //
// ./src/app/buffer-graph/services/buffer-graph-data-exchange.service.ts